/**
 * Helpers for hour : minute : second admin entry, with API payloads in total seconds.
 */

export interface HmsParts {
  hours: number;
  minutes: number;
  seconds: number;
}

export function secondsToHmsParts(totalSeconds: number): HmsParts {
  const sec = Math.max(0, Math.floor(totalSeconds));
  return {
    hours: Math.floor(sec / 3600),
    minutes: Math.floor((sec % 3600) / 60),
    seconds: sec % 60,
  };
}

export function hmsPartsToSeconds(parts: HmsParts): number {
  return parts.hours * 3600 + parts.minutes * 60 + parts.seconds;
}

export type ParseHmsInputResult =
  | { ok: true; seconds: number | undefined }
  | { ok: false; message: string };

/**
 * Parse three input strings into total seconds.
 * When `allowAllEmpty` is true and all parts are blank, returns `seconds: undefined`.
 */
export function parseHmsInputStrings(
  hoursStr: string,
  minutesStr: string,
  secondsStr: string,
  options?: { allowAllEmpty?: boolean }
): ParseHmsInputResult {
  const hTrim = hoursStr.trim();
  const mTrim = minutesStr.trim();
  const sTrim = secondsStr.trim();

  if (options?.allowAllEmpty && !hTrim && !mTrim && !sTrim) {
    return { ok: true, seconds: undefined };
  }

  const hours = hTrim === '' ? 0 : parseInt(hTrim, 10);
  const minutes = mTrim === '' ? 0 : parseInt(mTrim, 10);
  const seconds = sTrim === '' ? 0 : parseInt(sTrim, 10);

  if ([hours, minutes, seconds].some((v) => Number.isNaN(v) || v < 0)) {
    return { ok: false, message: 'Enter valid non-negative numbers' };
  }
  if (minutes > 59 || seconds > 59) {
    return { ok: false, message: 'Minutes and seconds must be between 0 and 59' };
  }

  return { ok: true, seconds: hours * 3600 + minutes * 60 + seconds };
}

/**
 * Movie API expects `runtime` in whole minutes. Round total seconds to the nearest minute,
 * minimum 1 (matches existing "at least 1 minute" behaviour after conversion).
 */
export function secondsToRuntimeMinutes(totalSeconds: number): number {
  return Math.max(1, Math.round(Math.max(0, totalSeconds) / 60));
}
